<script setup>
import { useAppStore } from '@/app/stores/app.store.js';
import ModalProvider from '@/app/components/layout/ModalProvider.vue';
import { ProgressProvider } from '@bprogress/vue';
import ApplicationHydrationError from '@/app/components/core/ApplicationHydrationError.vue';
import ApplicationHydrationLoader from '@/app/components/core/ApplicationHydrationLoader.vue';
import { storeToRefs } from 'pinia';
import { useLoader } from '@/shared/stores/loader.store.js';
import { computed } from 'vue';
import CFMobileNotice from '@/shared/components/CFMobileNotice.vue';
import { useSidebarAutoCollapse } from '@/app/composables/useSidebarAutoCollapse.js';
import { useRoute } from 'vue-router';
import { layouts } from '@/app/layouts/index.js';
import ConfirmDialog from '@/app/components/layout/ConfirmDialog.vue';

const appStore = useAppStore();
const loader = useLoader();
const vRoute = useRoute();

const { initializing, hydrating, error } = storeToRefs(appStore);

// Auto-collapse sidebar when window width < 1280px
useSidebarAutoCollapse();

const isMobile = computed(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
});

const currentLayout = computed(() => {
    const layoutName = vRoute.meta.layout === null ? 'blank' : vRoute.meta.layout || 'app';

    if (!layouts[layoutName]) {
        console.error(
            `[Layout error] Layout "${layoutName}" does not exist. ` +
                `Available layouts: ${Object.keys(layouts).join(', ')}`
        );

        return layouts.app;
    }

    return layouts[layoutName];
});
</script>

<template>
    <ProgressProvider id="cyberfusion">
        <Transition
            name="fade"
            mode="out-in"
            appear
        >
            <ApplicationHydrationLoader v-if="initializing || hydrating || loader.loading" />

            <ApplicationHydrationError
                v-else-if="error"
                :error="error"
            />
        </Transition>

        <CFMobileNotice v-if="isMobile" />

        <component
            :is="currentLayout"
            v-else-if="!initializing && !error"
        >
            <RouterView v-slot="{ Component, route }">
                <template v-if="route.meta.transition">
                    <Transition
                        :name="route.meta.transition"
                        mode="out-in"
                    >
                        <component
                            :is="Component"
                            :key="route.fullPath"
                        />
                    </Transition>
                </template>
                <template v-else>
                    <component
                        :is="Component"
                        :key="route.fullPath"
                    />
                </template>
            </RouterView>
        </component>

        <ConfirmDialog />
        <ModalProvider />
    </ProgressProvider>
</template>
